import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Plus, Trash2, Loader2 } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useAuth } from '@/contexts/AuthContext';
import { getConversations, createConversation, deleteConversation } from '../../api/assistant';
import { useAssistantPanel } from '../../contexts/AssistantPanelContext';
import { getLastModelId } from '../../lib/lastModel';
import type { Conversation } from '../../types';

export function ConversationList() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { selectedConversationId, setSelectedConversationId } = useAssistantPanel();

  const { data: conversations, isLoading } = useQuery({
    queryKey: ['assistant-conversations'],
    queryFn: getConversations,
  });

  const createMutation = useMutation({
    // Defaults new conversations to whatever model the admin last picked, rather than resetting
    // to the platform default every time.
    mutationFn: () => createConversation({ modelId: getLastModelId(user?.id) }),
    onSuccess: (conv) => {
      queryClient.invalidateQueries({ queryKey: ['assistant-conversations'] });
      setSelectedConversationId(conv.id);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteConversation,
    onSuccess: (_data, deletedId) => {
      queryClient.invalidateQueries({ queryKey: ['assistant-conversations'] });
      if (selectedConversationId === deletedId) {
        setSelectedConversationId(null);
      }
    },
  });

  return (
    <div className="flex flex-col h-full">
      <div className="p-3">
        <Button
          className="w-full"
          onClick={() => createMutation.mutate()}
          disabled={createMutation.isPending}
          data-testid="new-conversation-button"
        >
          {createMutation.isPending ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Plus className="h-4 w-4 mr-2" />
          )}
          New Conversation
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto">
        {isLoading && (
          <div className="flex justify-center p-4">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        )}

        {conversations?.length === 0 && !isLoading && (
          <p className="text-sm text-muted-foreground text-center p-4">
            No conversations yet
          </p>
        )}

        {conversations?.map((conv: Conversation) => (
          <button
            key={conv.id}
            onClick={() => setSelectedConversationId(conv.id)}
            className={`group w-full text-left px-3 py-2.5 border-b hover:bg-muted/50 transition-colors flex items-center justify-between gap-2 ${
              selectedConversationId === conv.id ? 'bg-muted' : ''
            }`}
          >
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium truncate">{conv.title || 'Untitled'}</p>
              <p className="text-xs text-muted-foreground">
                {formatDistanceToNow(new Date(conv.updatedAt), { addSuffix: true })}
              </p>
              {conv.modelId && (
                <p className="text-xs text-muted-foreground truncate">{conv.modelId.split('.').pop()?.replace(/-v\d+:\d+$/, '') ?? conv.modelId}</p>
              )}
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0 shrink-0 opacity-0 group-hover:opacity-100 hover:opacity-100 hover:text-destructive"
              onClick={(e) => {
                e.stopPropagation();
                deleteMutation.mutate(conv.id);
              }}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </button>
        ))}
      </div>
    </div>
  );
}
